import React, { useState, useEffect } from "react";
import { Card, CardContent, CardActions, Typography, Button, LinearProgress, Box, Chip, Tooltip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, CircularProgress } from "@mui/material";
import {
    PlayArrow,
    Stop,
    TipsAndUpdates,
    RocketLaunch,
    Source,
    PeopleAlt,
    WarningAmber,
    ListAlt,
    Storefront
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {SERVER_PLUGINS_ROUTE, LOG_ROUTE, SERVER_SETTINGS, SHOP_ITEMS, SERVER_PLAYERS} from "../utils/consts";
import { jobServer, wipeServer, statusServer } from "../http/ServerAPI";
import { motion } from "framer-motion";
import { Settings } from "@mui/icons-material";

const ServerCard = ({ server, refreshServers }) => {
    const navigate = useNavigate();
    const [loadingButton, setLoadingButton] = useState(null);
    const [confirm, setConfirm] = useState({ open: false, action: null, text: "", serverId: null });
    const [loadingProgress, setLoadingProgress] = useState(null);
    const [polling, setPolling] = useState(false);

    const handleConfirm = (text, action, serverId) => {
        setConfirm({ open: true, action, text, serverId });
    };

    const handleConfirmClose = () => {
        setConfirm({ open: false, action: null, text: "", serverId: null });
    };

    const handleConfirmedAction = async () => {
        if (!confirm.action) return;
        setLoadingButton(confirm.action);
        handleConfirmClose();

        try {
            if (confirm.action === "wipe") {
                await wipeServer(confirm.serverId);
                await refreshServers(); // ✅ можно обновить после вайпа сразу
            } else {
                await jobServer(confirm.serverId, confirm.action);

                if (confirm.action === "start") {
                    startMonitoring(confirm.serverId); // ✅ начинаем мониторить запуск сервера
                } else {
                    await refreshServers(); // ✅ обновляем только если НЕ старт
                }
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingButton(null);
        }
    };

    const startMonitoring = (serverId) => {
        if (polling) return;

        setPolling(true);
        server.status_id = 5;
        const intervalId = setInterval(async () => {
            try {
                const res = await statusServer(serverId);
                if (res.result && res.data) {
                    const statusText = res.data.status;
                    if (statusText.startsWith("Loading:")) {
                        const percent = parseInt(statusText.split(":")[1]);
                        setLoadingProgress(percent);
                    }
                    if (!statusText.startsWith("Loading")) {
                        // Сервер запустился
                        clearInterval(intervalId);
                        setPolling(false);
                        setLoadingProgress(null);
                        await refreshServers();
                    }
                }
            } catch (e) {
                console.error(e);
            }
        }, 10000);
    };



    const isRunning = server.status_id === 3;
    const isStopped = server.status_id === 4 || server.status_id === 8;
    const isStarting = server.status_id === 5;

    const memTotal = 64; // Всего памяти 64GB
    const memUsed = server.mem.toFixed(2);

    const playersCountText = (count) => {
        if (count % 10 === 1 && count % 100 !== 11) return `${count} игрок`;
        if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) return `${count} игрока`;
        return `${count} игроков`;
    };

    const getProgressColor = (progress) => {
        if (progress < 50) return "#55cafb"; // голубой
        if (progress < 80) return "#41c1fb"; // светло-зеленый
        return "#29b6f6"; // насыщенный зеленый
    };



    return (
        <>
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                style={{ height: "100%" }}
            >
                <Card sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                    <Box p={1} bgcolor={isRunning ? "success.main" : isStarting ? "info.main" : "grey.600"} color="white" textAlign="center" borderTopLeftRadius="4px" borderTopRightRadius="4px">
                        {isRunning ? "Работает" : isStarting ? `Запуск ${loadingProgress !== null ? `(${loadingProgress}%)` : ""}` : "Остановлен"}
                    </Box>
                    {isStarting && loadingProgress !== null && (
                        <LinearProgress
                            variant="determinate"
                            value={loadingProgress}
                            sx={{
                                height: 6,
                                borderRadius: 0,
                                backgroundColor: "grey.400",
                                "& .MuiLinearProgress-bar": {
                                    backgroundColor: getProgressColor(loadingProgress)
                                }
                            }}
                        />
                    )}
                    <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                        <Typography variant="h6" gutterBottom>{server.title}</Typography>
                        <Typography variant="body2" color="textSecondary">{server.description}</Typography>

                        <Box mt={2}>
                            <Typography variant="body2">CPU: {server.cpu.toFixed(2)}%</Typography>
                            <LinearProgress variant="determinate" value={server.cpu} sx={{ height: 6, borderRadius: 2 }} />
                        </Box>

                        <Box mt={1}>
                            <Typography variant="body2">MEM: {memUsed} ГБ / {memTotal} ГБ</Typography>
                            <LinearProgress variant="determinate" value={(memUsed / memTotal) * 100} sx={{ height: 6, borderRadius: 2 }} />
                        </Box>

                        <Box mt={1}>
                            <Typography variant="body2">CPU temp: {server.cpu_temperature.toFixed(2)} °C</Typography>
                            <LinearProgress variant="determinate" value={(server.cpu_temperature / 80) * 100} sx={{ height: 6, borderRadius: 2 }} />
                        </Box>

                        <Box mt={1}>
                            <Typography variant="body2">SSD temp: {server.ssd_temperature.toFixed(2)} °C</Typography>
                            <LinearProgress variant="determinate" value={(server.ssd_temperature / 80) * 100} sx={{ height: 6, borderRadius: 2 }} />
                        </Box>

                        <Box mt={2}>
                            <Typography variant="body2">Oxide: {server.installed} / {server.required}</Typography>
                            {server.required !== server.installed && (
                                <Chip label="Требуется обновление Oxide" color="warning" size="small" icon={<WarningAmber />} sx={{ mt: 1 }} />
                            )}
                        </Box>

                        <Box mt={2} display="flex" alignItems="center">
                            <PeopleAlt fontSize="small" sx={{ mr: 1 }} />
                            <Typography variant="body2">{playersCountText(server.online)}</Typography>
                        </Box>

                        {server.player_names.length > 0 && (
                            <Box mt={1} maxHeight={80} overflow="auto">
                                <Typography variant="body2" fontSize="small">{server.player_names.join(", ")}</Typography>
                            </Box>
                        )}
                    </CardContent>

                    <CardActions sx={{ flexWrap: 'wrap', justifyContent: 'center' }}>
                        {isStopped && (
                            <Button size="small" color="success" startIcon={loadingButton === "start" ? <CircularProgress size={20} /> : <PlayArrow />}
                                    onClick={() => handleConfirm("Запустить сервер?", "start", server.server_id)} disabled={!!loadingButton}>
                                Старт
                            </Button>
                        )}
                        {isRunning && (
                            <Button size="small" color="error" startIcon={loadingButton === "stop" ? <CircularProgress size={20} /> : <Stop />}
                                    onClick={() => handleConfirm("Остановить сервер?", "stop", server.server_id)} disabled={!!loadingButton}>
                                Стоп
                            </Button>
                        )}
                        <Button size="small" color="info" startIcon={loadingButton === "wipe" ? <CircularProgress size={20} /> : <RocketLaunch />}
                                onClick={() => handleConfirm("Выполнить вайп сервера?", "wipe", server.server_id)} disabled={!!loadingButton}>
                            Вайп
                        </Button>
                        <Button size="small" color="primary" startIcon={<Source />}
                                onClick={() => navigate(SERVER_PLUGINS_ROUTE + '/' + server.server_id)}>
                            Плагины
                        </Button>
                        <Button size="small" color="secondary" startIcon={<ListAlt />}
                                onClick={() => navigate(LOG_ROUTE + '/' + server.server_id)}>
                            Логи
                        </Button>
                        <Button
                            size="small"
                            color="secondary"
                            startIcon={<Settings />}
                            onClick={() => navigate(SERVER_SETTINGS + '/' + server.server_id)}
                        >
                            Управление
                        </Button>
                        <Button
                            size="small"
                            color="warning"
                            startIcon={<Storefront />}
                            onClick={() => navigate(SHOP_ITEMS + '/' + server.server_id)}
                        >
                            Магазин
                        </Button>
                        <Button
                            size="small"
                            color="info"
                            startIcon={<PeopleAlt />}
                            onClick={() => navigate(SERVER_PLAYERS  + '/' + server.server_id)}
                        >
                            Игроки
                        </Button>
                    </CardActions>
                </Card>
            </motion.div>

            <Dialog open={confirm.open} onClose={handleConfirmClose}>
                <DialogTitle>Подтверждение действия</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirm.text}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConfirmClose}>Отмена</Button>
                    <Button onClick={handleConfirmedAction} color="primary" autoFocus>Подтвердить</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ServerCard;
